import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { soccerPath, trainingPath } from '../utils/paths';
import PhotoGallery from './PhotoGallery';
import './PublicTrainingLog.css';

const PILLAR_LABELS = {
  STRENGTH: 'Strength',
  STAMINA: 'Stamina',
  SKILL: 'Skill',
};

const PublicTrainingLog = () => {
  const { token } = useParams();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLog = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getPublicTrainingLog(token);
      setLog(data);
    } catch (err) {
      setError(err.message || 'Failed to load training log');
      setLog(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(`${dateString}T12:00:00`).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleWhatsApp = () => {
    if (!log) return;
    const url = api.shareTrainingLogViaWhatsApp({
      athleteFirstName: log.athleteFirstName,
      date: log.date,
      completedCount: log.completedCount,
      totalCount: log.totalCount,
      shareToken: token,
    });
    window.open(url, '_blank');
  };

  if (loading) {
    return <div className="public-training loading">Loading training log…</div>;
  }

  if (error) {
    return (
      <div className="public-training">
        <div className="card">
          <div className="alert alert-error">{error}</div>
          <Link to={soccerPath('/')} className="btn btn-secondary">
            Back to Soccer
          </Link>
        </div>
      </div>
    );
  }

  if (!log) {
    return null;
  }

  return (
    <div className="public-training">
      <header className="public-training__hero">
        <p className="public-training__eyebrow">Shared training day</p>
        <h1>
          {log.athleteFirstName}&apos;s session
        </h1>
        <p>{formatDate(log.date)}</p>
        <p className="public-training__summary">
          {log.completedCount}/{log.totalCount} activities completed
        </p>
        <div className="public-training__actions">
          <button type="button" className="btn btn-primary" onClick={handleWhatsApp}>
            Share via WhatsApp
          </button>
          <Link to={trainingPath()} className="btn btn-secondary">
            Open Training
          </Link>
        </div>
      </header>

      <section className="public-training__panel">
        <h2>Activities</h2>
        <ul className="public-training__list">
          {(log.entries || []).map((entry, index) => (
            <li key={`${entry.title}-${index}`}>
              <span className={`training-pillar training-pillar--${(entry.pillar || '').toLowerCase()}`}>
                {PILLAR_LABELS[entry.pillar] || entry.pillar}
              </span>
              <div>
                <strong>{entry.title}</strong>
                <p>
                  {entry.status?.toLowerCase()}
                  {entry.actualDurationMin != null ? ` · ${entry.actualDurationMin} min` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>

        {log.notes && (
          <div className="public-training__notes">
            <h3>Notes</h3>
            <p>{log.notes}</p>
          </div>
        )}

        {log.photoUrls?.length > 0 && (
          <div className="public-training__photos">
            <h3>Photos</h3>
            <PhotoGallery photos={log.photoUrls} />
          </div>
        )}
      </section>
    </div>
  );
};

export default PublicTrainingLog;
